/*
 *  "Wrong label?" — a reviewer corrects (or confirms) the agent-posture label
 *  on one Fresh listing and may teach the phrase that proves it.
 *
 *  POST /api/fresh/listings/{source}/{id}/label-report. Applies immediately
 *  for everyone; the background labeller never overwrites a human verdict.
 *
 *  ``inline`` renders the dialog as an absolutely-positioned layer inside the
 *  caller's container instead of the page-level Modal: the Fresh Deck is a
 *  z-[1400] portal, so a z-50 Modal would open underneath it.
 */
import { useEffect, useMemo, useState } from "react";
import { api, ApiError } from "@/api/client";
import { Modal } from "@/components/Modal";
import {
  AGENT_LABEL_META,
  type AgentLabel,
  type AgentLabelFields,
} from "@/components/AgentLabelBadge";

type Choice = AgentLabel | "none";

const CHOICES: { value: Choice; text: string; hint: string }[] = [
  { value: "no_agents", text: AGENT_LABEL_META.no_agents.text, hint: "Owner refuses agents / agencies" },
  { value: "likely_agent", text: AGENT_LABEL_META.likely_agent.text, hint: "Reads like an agent posting as owner" },
  { value: "agents_welcome", text: AGENT_LABEL_META.agents_welcome.text, hint: "Owner invites agents or offers commission" },
  { value: "none", text: "Nothing relevant", hint: "The text says nothing about agents" },
];

export interface LabelReportResult extends AgentLabelFields {
  ok: boolean;
  phrase_found: boolean | null;
}

export interface LabelReportDialogProps {
  source: "ss" | "mh";
  externalId: number;
  current: AgentLabelFields;
  inline?: boolean;
  onClose: () => void;
  onSaved: (fields: AgentLabelFields, phraseFound: boolean | null) => void;
}

/** Text the user highlighted on the card right before opening the dialog —
 *  the natural way to say "this sentence is the proof". */
function selectedText(): string {
  try {
    const s = window.getSelection()?.toString().trim() ?? "";
    return s.length >= 4 && s.length <= 200 ? s : "";
  } catch {
    return "";
  }
}

export function LabelReportDialog(props: LabelReportDialogProps) {
  const { source, externalId, current, inline = false, onClose, onSaved } = props;
  const initialSelection = useMemo(selectedText, []);
  const [choice, setChoice] = useState<Choice>(current.agent_label ?? "none");
  const [phrase, setPhrase] = useState(initialSelection);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  useEffect(() => {
    if (!inline) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [inline, onClose]);

  const unchanged = choice === (current.agent_label ?? "none");
  const confirming = unchanged && !!current.agent_label && phrase.trim() === "";
  // A phrase may teach any choice. For "Nothing relevant" it is guidance only:
  // the model is shown the wording as something that proves nothing.
  const phraseAllowed = true;
  const teachingNone = choice === "none";

  async function save() {
    setSaving(true);
    setMsg(null);
    try {
      const body = confirming
        ? { confirm: true }
        : { label: choice, phrase: phraseAllowed && phrase.trim() ? phrase.trim() : null };
      const r = await api<LabelReportResult>(
        `/api/fresh/listings/${source}/${externalId}/label-report`,
        { method: "POST", json: body },
      );
      onSaved(
        {
          agent_label: r.agent_label,
          agent_label_confidence: r.agent_label_confidence,
          agent_label_reason: r.agent_label_reason,
          agent_label_evidence: r.agent_label_evidence,
          agent_label_source: r.agent_label_source,
        },
        r.phrase_found,
      );
    } catch (e) {
      setMsg(e instanceof ApiError ? e.message : "Could not save — try again.");
      setSaving(false);
    }
  }

  const dark = inline;
  const radioCls = (active: boolean) =>
    dark
      ? `flex items-start gap-2.5 rounded-xl border px-3 py-2.5 text-left transition ${
          active ? "border-white/40 bg-white/[0.10]" : "border-white/10 bg-white/[0.04] hover:bg-white/[0.07]"
        }`
      : `flex items-start gap-2.5 rounded-xl border px-3 py-2.5 text-left transition ${
          active ? "border-accent-500/60 bg-accent-500/10" : "border-white/10 bg-white/[0.02] hover:bg-white/[0.05]"
        }`;
  const textMain = dark ? "text-white" : "text-slate-100";
  const textDim = dark ? "text-white/50" : "text-slate-500";

  const body = (
    <div className="space-y-3">
      <div className={`text-[12px] ${textDim}`}>
        {current.agent_label
          ? `Currently labelled “${AGENT_LABEL_META[current.agent_label].text}”` +
            (current.agent_label_evidence ? ` because of “${current.agent_label_evidence}”.` : ".")
          : "This listing has no agent label yet."}{" "}
        Pick what the text really says — it updates for everyone right away.
      </div>
      <div className="grid gap-1.5" role="radiogroup">
        {CHOICES.map((c) => {
          const active = choice === c.value;
          return (
            <button
              key={c.value}
              type="button"
              role="radio"
              aria-checked={active}
              onClick={() => setChoice(c.value)}
              className={radioCls(active)}
            >
              <span
                className={`mt-0.5 h-3.5 w-3.5 rounded-full border flex-shrink-0 ${
                  active ? (dark ? "border-white bg-white" : "border-accent-400 bg-accent-500") : "border-current opacity-40"
                }`}
              />
              <span className="min-w-0">
                <span className={`block text-[13px] font-semibold ${textMain}`}>{c.text}</span>
                <span className={`block text-[11px] ${textDim}`}>{c.hint}</span>
              </span>
            </button>
          );
        })}
      </div>
      {phraseAllowed && (
        <label className="block">
          <span className={`block text-[11px] font-medium mb-1 ${textDim}`}>
            {teachingNone ? "Which words did the AI wrongly treat as proof?" : "Which words prove it?"}{" "}
            <span className="opacity-60">(optional — teaches the system)</span>
          </span>
          <textarea
            value={phrase}
            onChange={(e) => setPhrase(e.target.value)}
            rows={2}
            maxLength={200}
            placeholder={teachingNone ? "e.g. ვარ მეპატრონე" : "e.g. სააგენტოებთან არ ვთანამშრომლობ"}
            className={
              dark
                ? "w-full rounded-lg border border-white/15 bg-white/[0.06] px-3 py-2 text-[12.5px] text-white placeholder:text-white/30 focus:outline-none focus:border-white/40"
                : "input w-full text-[12.5px]"
            }
          />
          <span className={`block text-[10.5px] mt-1 ${textDim}`}>
            {teachingNone
              ? "The AI's own quote is remembered automatically as a mistake; add words here only if there is more wording it should ignore."
              : "Tip: highlight the sentence on the card before clicking “Wrong label?” and it lands here. Future listings containing it get this label automatically."}
          </span>
        </label>
      )}
      {msg && <div className="text-[12px] text-rose-400">{msg}</div>}
    </div>
  );

  const footer = (
    <div className="flex items-center justify-end gap-2">
      <button
        type="button"
        onClick={onClose}
        className={
          dark
            ? "px-3 py-2 text-[12.5px] rounded-lg text-white/60 hover:text-white hover:bg-white/[0.08]"
            : "btn-secondary"
        }
      >
        Cancel
      </button>
      <button
        type="button"
        onClick={() => void save()}
        disabled={saving || (unchanged && !current.agent_label && !phrase.trim())}
        className={
          dark
            ? "px-3.5 py-2 text-[12.5px] rounded-lg font-semibold bg-white text-black hover:bg-white/90 disabled:opacity-40"
            : "btn-primary disabled:opacity-40"
        }
      >
        {saving ? "Saving…" : confirming ? "Confirm label" : "Save label"}
      </button>
    </div>
  );

  if (!inline) {
    return (
      <Modal title="Wrong label?" subtitle="Correct the AI's read of this listing" onClose={onClose} footer={footer}>
        {body}
      </Modal>
    );
  }

  return (
    <div className="absolute inset-0 z-50 flex items-end sm:items-center justify-center p-3" onClick={onClose}>
      <div className="absolute inset-0 bg-black/60 backdrop-blur-[2px] animate-[fadeIn_.2s_ease]" />
      <div
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-sm rounded-2xl border border-white/15 bg-[#101014]/95 backdrop-blur-xl shadow-2xl p-4 space-y-3 animate-[deckSheetUp_.28s_cubic-bezier(.2,.9,.3,1.05)]"
      >
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="text-[14.5px] font-semibold text-white">Wrong label?</div>
            <div className="text-[11px] text-white/45">Correct the AI's read of this listing</div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="h-7 w-7 rounded-full bg-white/[0.07] border border-white/10 text-white/70 hover:text-white flex items-center justify-center text-base leading-none"
          >
            ×
          </button>
        </div>
        {body}
        {footer}
      </div>
    </div>
  );
}
